// src/components/layout/Layout.jsx
import React, { useContext, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import Topbar from './Topbar';
import Sidebar from './Sidebar';
import { AuthContext } from '../../context/AuthContext';
import socket from '../../services/socket';
import { playAuthRequestSound, playAuthCompletedSound } from '../../utils/sound';
import useHotkeys from '../../hooks/useHotkeys';
import ShortcutsHelp from '../ShortcutsHelp';

export default function Layout({ children, theme, onToggleTheme, onOpenSearch }) {
    const { user } = useContext(AuthContext);
    const navigate = useNavigate();
    const [showShortcuts, setShowShortcuts] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(false);          // overlay móvil
    const [collapsed, setCollapsed] = useState(() => {             // oculto en escritorio (persistido)
        try { return localStorage.getItem('sidebarCollapsed') === '1'; } catch { return false; }
    });

    // La hamburguesa del topbar: en móvil abre/cierra el overlay; en escritorio
    // oculta/muestra el sidebar y recuerda la preferencia.
    const toggleSidebar = () => {
        if (typeof window !== 'undefined' && window.innerWidth <= 992) {
            setSidebarOpen(v => !v);
        } else {
            setCollapsed(v => {
                const next = !v;
                try { localStorage.setItem('sidebarCollapsed', next ? '1' : '0'); } catch {}
                return next;
            });
        }
    };

    // ── Atajos globales (no actúan mientras se escribe en un campo) ────────
    // La lista completa está en components/ShortcutsHelp.jsx (se abre con `?`).
    const puedeAutorizar = user?.role === 'authorizer' || user?.role === 'admin';
    const sinModalAbierto = () => !document.querySelector('.modal.show');
    useHotkeys([
        { keys: '/',   run: () => onOpenSearch?.(),            when: sinModalAbierto },
        { keys: 'g t', run: () => navigate('/tickets'),        when: sinModalAbierto },
        { keys: 'g d', run: () => navigate('/dashboard'),      when: sinModalAbierto },
        { keys: 'g a', run: () => navigate('/authorizations'), when: () => puedeAutorizar && sinModalAbierto() },
        { keys: 'n',   run: () => navigate('/tickets/new'),    when: sinModalAbierto },
        { keys: '?',   run: () => setShowShortcuts(v => !v) },
    ]);

    // Evita sonidos duplicados para el mismo ticket dentro de la misma sesión
    const seenRequestsRef = useRef(new Set());

    useEffect(() => {
        if (!user) return;

        // ── Nueva solicitud de autorización → notificar al autorizador ──────────
        const onNewAuthRequest = (ticket) => {
            const uid = String(user.id);

            const isAuth1 = String(ticket.authorizer?._id || ticket.authorizer || '') === uid;
            const isAuth2 = String(ticket.authorizer2?._id || ticket.authorizer2 || '') === uid;
            if (!isAuth1 && !isAuth2) return;

            // Deduplicar dentro de la sesión
            const key = `${ticket._id}-${ticket.authorizationStatus}`;
            if (seenRequestsRef.current.has(key)) return;
            seenRequestsRef.current.add(key);

            playAuthRequestSound();
            toast.warning(`📋 Nueva solicitud de autorización: #${ticket.folio} — ${ticket.title}`, {
                autoClose: 8000,
            });
        };

        // ── Autorización completada → notificar a creador y agente ─────────────
        const onAuthCompleted = (ticket) => {
            const uid = String(user.id);

            const isCreator = String(ticket.createdBy?._id || ticket.createdBy || '') === uid;
            const isAssignee = String(ticket.assignedTo?._id || ticket.assignedTo || '') === uid;
            if (!isCreator && !isAssignee) return;

            const label = ticket.authorizationStatus === 'approved' ? '✅ aprobado' : '❌ rechazado';
            playAuthCompletedSound();
            toast.info(`Ticket #${ticket.folio} fue ${label}`, { autoClose: 8000 });
        };

        socket.on('newAuthorizationRequest', onNewAuthRequest);
        socket.on('authorizationCompleted', onAuthCompleted);

        // Unirse a rooms por departamento y al room personal — habilita eventos
        // de equipo (userBreakChanged) y eventos dirigidos (chat, notificaciones).
        // Re-emitir en cada reconexión para no perder pertenencia al room tras un cierre.
        const deptIds = (user.departments || []).map(d => d?._id || d).filter(Boolean);
        const joinRooms = () => {
            if (user.id) socket.emit('joinUser', user.id);
            if (deptIds.length) socket.emit('joinDepartments', deptIds);
        };
        joinRooms();
        socket.on('connect', joinRooms);

        return () => {
            socket.off('newAuthorizationRequest', onNewAuthRequest);
            socket.off('authorizationCompleted', onAuthCompleted);
            socket.off('connect', joinRooms);
        };
    }, [user]);

    return (
        <>
            <Sidebar open={sidebarOpen} collapsed={collapsed} onClose={() => setSidebarOpen(false)} />
            <div className={`app-content${collapsed ? ' sb-collapsed' : ''}`}>
                <Topbar
                    theme={theme}
                    onToggleTheme={onToggleTheme}
                    onOpenSearch={onOpenSearch}
                    onToggleSidebar={toggleSidebar}
                    onOpenShortcuts={() => setShowShortcuts(true)}
                />
                <main className="page-content">{children}</main>
            </div>
            <ShortcutsHelp show={showShortcuts} onClose={() => setShowShortcuts(false)} />
        </>
    );
}